import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Plus, Trash2 } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { ParameterCell } from './ParameterCell';
function mkParam(overrides) {
    return {
        label: overrides.name,
        paramType: 'quantity',
        source: 'contractor',
        value: null,
        perPeriod: false,
        ...overrides
    };
}
function InlineFteEditor({ value, onChange }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const handleStart = useCallback(()=>{
        setEditValue(value != null ? String(value) : '');
        setIsEditing(true);
    }, [
        value
    ]);
    const handleBlur = useCallback(()=>{
        setIsEditing(false);
        if (editValue === '') {
            onChange(undefined);
            return;
        }
        const parsed = Number(editValue);
        if (Number.isNaN(parsed) || parsed < 0) return;
        if (parsed !== value) onChange(parsed);
    }, [
        editValue,
        value,
        onChange
    ]);
    const handleKeyDown = useCallback((e)=>{
        if (e.key === 'Enter') e.target.blur();
        else if (e.key === 'Escape') setIsEditing(false);
    }, []);
    if (isEditing) {
        return /*#__PURE__*/ _jsx("input", {
            type: "number",
            value: editValue,
            onChange: (e)=>setEditValue(e.target.value),
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
            className: "w-full px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-right",
            step: "any",
            min: "0"
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "px-2 py-1.5 text-sm text-right rounded cursor-pointer hover:bg-gray-50",
        onClick: handleStart,
        children: value != null ? value : '—'
    });
}
function formatCurrency(value) {
    if (value === 0) return '—';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
    return `$${value.toLocaleString(undefined, {
        maximumFractionDigits: 0
    })}`;
}
export const TierCompositionEditor = /*#__PURE__*/ memo(function TierCompositionEditor({ tier, rateCard, columns, periods, onTierChange }) {
    const [showAddRole, setShowAddRole] = useState(false);
    const group = tier.group;
    const lines = group.lines;
    // IDs of labor categories already in this tier
    const assignedIds = useMemo(()=>new Set(lines.map((l)=>l.laborCategoryId).filter(Boolean)), [
        lines
    ]);
    // Available categories from rate card not already assigned
    const availableCategories = useMemo(()=>{
        if (!rateCard) return [];
        return rateCard.categories.filter((cat)=>!assignedIds.has(cat.laborCategoryId));
    }, [
        rateCard,
        assignedIds
    ]);
    // Current FTE total
    const currentFtes = useMemo(()=>lines.reduce((sum, l)=>sum + (l.fteCount ?? 0), 0), [
        lines
    ]);
    const handleAddLine = useCallback((laborCategoryId, title)=>{
        const newLine = {
            id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            laborCategoryId,
            label: title,
            parameters: {
                hours: mkParam({
                    name: 'hours',
                    label: 'Hours',
                    paramType: 'quantity',
                    source: 'contractor'
                }),
                rate: mkParam({
                    name: 'rate',
                    label: 'Rate',
                    paramType: 'rate',
                    source: 'computed'
                }),
                total: mkParam({
                    name: 'total',
                    label: 'Total',
                    paramType: 'currency',
                    source: 'computed',
                    formula: {
                        type: 'multiply',
                        operands: [
                            'hours',
                            'rate'
                        ]
                    }
                })
            }
        };
        const updatedGroup = {
            ...group,
            lines: [
                ...group.lines,
                newLine
            ]
        };
        onTierChange({
            ...tier,
            group: updatedGroup
        });
        setShowAddRole(false);
    }, [
        group,
        tier,
        onTierChange
    ]);
    const handleRemoveLine = useCallback((lineId)=>{
        const updatedGroup = {
            ...group,
            lines: group.lines.filter((l)=>l.id !== lineId)
        };
        onTierChange({
            ...tier,
            group: updatedGroup
        });
    }, [
        group,
        tier,
        onTierChange
    ]);
    const handleFteChange = useCallback((lineId, fteCount)=>{
        const updatedGroup = {
            ...group,
            lines: group.lines.map((l)=>l.id === lineId ? {
                    ...l,
                    fteCount
                } : l)
        };
        onTierChange({
            ...tier,
            group: updatedGroup
        });
    }, [
        group,
        tier,
        onTierChange
    ]);
    const handleParamChange = useCallback((lineId, paramName, value, period)=>{
        const updatedGroup = {
            ...group,
            lines: group.lines.map((l)=>{
                if (l.id !== lineId) return l;
                const param = l.parameters[paramName];
                if (!param) return l;
                let updatedParam;
                if (period && param.perPeriod) {
                    updatedParam = {
                        ...param,
                        periodValues: {
                            ...param.periodValues,
                            [period]: value ?? 0
                        }
                    };
                } else {
                    updatedParam = {
                        ...param,
                        value
                    };
                }
                return {
                    ...l,
                    parameters: {
                        ...l.parameters,
                        [paramName]: updatedParam
                    }
                };
            })
        };
        onTierChange({
            ...tier,
            group: updatedGroup
        });
    }, [
        group,
        tier,
        onTierChange
    ]);
    // Build column headers
    const headerCols = [];
    for (const col of columns){
        if (col.perPeriod && periods.length > 0) {
            for (const period of periods){
                headerCols.push({
                    key: `${col.key}-${period}`,
                    label: `${col.label} (${period})`
                });
            }
        } else {
            headerCols.push({
                key: col.key,
                label: col.label
            });
        }
    }
    // Add action column
    headerCols.push({
        key: '_actions',
        label: ''
    });
    const { min: minFtes, max: maxFtes } = tier.fteConstraints;
    const fteWarning = minFtes != null && currentFtes < minFtes || maxFtes != null && currentFtes > maxFtes;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-sm font-semibold text-gray-700",
                        children: [
                            tier.tierLabel,
                            " (Tier ",
                            tier.tierNumber,
                            ")"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: `text-xs ${fteWarning ? 'text-red-500 font-medium' : 'text-gray-400'}`,
                        children: [
                            currentFtes,
                            " FTEs",
                            (minFtes != null || maxFtes != null) && /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    ' ',
                                    "/",
                                    ' ',
                                    minFtes != null && maxFtes != null && minFtes === maxFtes ? `${minFtes} required` : `${minFtes ?? '?'}–${maxFtes ?? '?'}`
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-xs text-gray-400",
                        children: [
                            lines.length,
                            " ",
                            lines.length === 1 ? 'role' : 'roles'
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full border-collapse",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsx("tr", {
                                className: "border-b-2 border-gray-200",
                                children: headerCols.map((h)=>/*#__PURE__*/ _jsx("th", {
                                        className: `px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider ${h.key.startsWith('label') ? 'text-left' : 'text-right'} ${h.key === '_actions' ? 'w-8' : ''}`,
                                        children: h.label
                                    }, h.key))
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: lines.map((line)=>/*#__PURE__*/ _jsxs("tr", {
                                    className: "border-b border-gray-100 hover:bg-gray-50/50 group",
                                    children: [
                                        columns.map((col)=>{
                                            if (col.key === 'label') {
                                                return /*#__PURE__*/ _jsx("td", {
                                                    className: "px-3 py-2 text-sm font-medium text-gray-900",
                                                    children: line.label
                                                }, col.key);
                                            }
                                            if (col.key === 'ftes') {
                                                return /*#__PURE__*/ _jsx("td", {
                                                    className: "px-1 py-1",
                                                    children: /*#__PURE__*/ _jsx(InlineFteEditor, {
                                                        value: line.fteCount,
                                                        onChange: (val)=>handleFteChange(line.id, val)
                                                    })
                                                }, col.key);
                                            }
                                            const param = line.parameters[col.parameterName];
                                            if (!param) {
                                                return /*#__PURE__*/ _jsx("td", {
                                                    className: "px-1 py-1 text-sm text-gray-400 text-right",
                                                    children: "—"
                                                }, col.key);
                                            }
                                            if (col.perPeriod && periods.length > 0) {
                                                return periods.map((period)=>/*#__PURE__*/ _jsx("td", {
                                                        className: "px-1 py-1",
                                                        children: /*#__PURE__*/ _jsx(ParameterCell, {
                                                            param: param,
                                                            editable: col.editable,
                                                            period: period,
                                                            onChange: (val)=>handleParamChange(line.id, col.parameterName, val, period)
                                                        })
                                                    }, `${col.key}-${period}`));
                                            }
                                            return /*#__PURE__*/ _jsx("td", {
                                                className: "px-1 py-1",
                                                children: /*#__PURE__*/ _jsx(ParameterCell, {
                                                    param: param,
                                                    editable: col.editable,
                                                    onChange: (val)=>handleParamChange(line.id, col.parameterName, val)
                                                })
                                            }, col.key);
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-1 py-1 text-center",
                                            children: /*#__PURE__*/ _jsx("button", {
                                                onClick: ()=>handleRemoveLine(line.id),
                                                className: "text-gray-300 hover:text-red-500 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity",
                                                children: /*#__PURE__*/ _jsx(Trash2, {
                                                    className: "w-3.5 h-3.5"
                                                })
                                            })
                                        })
                                    ]
                                }, line.id))
                        })
                    ]
                })
            }),
            group.subtotal && /*#__PURE__*/ _jsxs("div", {
                className: "flex justify-end text-sm",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-gray-500 mr-2",
                        children: "Tier Subtotal:"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium text-gray-700",
                        children: formatCurrency(group.subtotal.value ?? 0)
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        onClick: ()=>setShowAddRole(!showAddRole),
                        disabled: availableCategories.length === 0,
                        className: "flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed",
                        children: [
                            /*#__PURE__*/ _jsx(Plus, {
                                className: "w-3.5 h-3.5"
                            }),
                            "Add Role"
                        ]
                    }),
                    showAddRole && availableCategories.length > 0 && /*#__PURE__*/ _jsx("div", {
                        className: "absolute top-6 left-0 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-48 overflow-auto min-w-[250px]",
                        children: availableCategories.map((cat)=>/*#__PURE__*/ _jsxs("button", {
                                onClick: ()=>handleAddLine(cat.laborCategoryId, cat.title),
                                className: "w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50",
                                children: [
                                    cat.title,
                                    cat.level && /*#__PURE__*/ _jsxs("span", {
                                        className: "text-xs text-gray-400 ml-1",
                                        children: [
                                            "(",
                                            cat.level,
                                            ")"
                                        ]
                                    })
                                ]
                            }, cat.laborCategoryId))
                    })
                ]
            })
        ]
    });
});
